import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import AwsApiClient from "../clients/awsApiClient.ts";
import type { ApiRequestConfig, ApiError } from "../clients/awsApiClient.ts";

export interface UseApiQueryOptions<T>
  extends Omit<UseQueryOptions<T, ApiError>, "queryFn"> {
  path: string;
  config?: ApiRequestConfig;
}

// Mutation options type alias - apiName is now hardcoded in AwsApiClient
export type UseApiMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, ApiError, TVariables>,
  "mutationFn"
>;

// GET request hook
export function useApiQuery<T>(options: UseApiQueryOptions<T>) {
  const { path, config, ...queryOptions } = options;

  return useQuery<T, ApiError>({
    ...queryOptions,
    queryFn: () => AwsApiClient.apiGet<T>(path, config),
  });
}

export function useApiPostQuery<T>(options: UseApiQueryOptions<T>) {
  const { path, config, ...queryOptions } = options;

  return useQuery<T, ApiError>({
    ...queryOptions,
    queryFn: () => AwsApiClient.apiPost<T>(path, config),
  });
}

// POST mutation hook
export function useApiPostMutation<TData = unknown, TVariables = unknown>(
  options: UseApiMutationOptions<TData, TVariables> & {
    getPath: (variables: TVariables) => string;
    getConfig?: (variables: TVariables) => ApiRequestConfig;
  }
) {
  const { getPath, getConfig, ...mutationOptions } = options;

  return useMutation<TData, ApiError, TVariables>({
    ...mutationOptions,
    mutationFn: (variables: TVariables) => {
      const path = getPath(variables);
      const config = getConfig?.(variables);
      return AwsApiClient.apiPost<TData>(path, config);
    },
  });
}

// PATCH mutation hook
export function useApiPatchMutation<TData = unknown, TVariables = unknown>(
  options: UseApiMutationOptions<TData, TVariables> & {
    getPath: (variables: TVariables) => string;
    getConfig?: (variables: TVariables) => ApiRequestConfig;
  }
) {
  const queryClient = useQueryClient();
  const { getPath, getConfig, ...mutationOptions } = options;

  return useMutation<TData, ApiError, TVariables>({
    ...mutationOptions,
    mutationFn: (variables: TVariables) => {
      const path = getPath(variables);
      const config = getConfig?.(variables);
      return AwsApiClient.apiPatch<TData>(path, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiFactorsApp"] });
    },
  });
}

// PUT mutation hook
export function useApiPutMutation<TData = unknown, TVariables = unknown>(
  options: UseApiMutationOptions<TData, TVariables> & {
    getPath: (variables: TVariables) => string;
    getConfig?: (variables: TVariables) => ApiRequestConfig;
  }
) {
  const queryClient = useQueryClient();
  const { getPath, getConfig, ...mutationOptions } = options;

  return useMutation<TData, ApiError, TVariables>({
    ...mutationOptions,
    mutationFn: (variables: TVariables) => {
      const path = getPath(variables);
      const config = getConfig?.(variables);
      return AwsApiClient.apiPut<TData>(path, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["apiFactorsApp"] });
    },
  });
}

// DELETE mutation hook
export function useApiDeleteMutation<TData = unknown, TVariables = unknown>(
  options: UseApiMutationOptions<TData, TVariables> & {
    getPath: (variables: TVariables) => string;
    getConfig?: (variables: TVariables) => ApiRequestConfig;
  }
) {
  const { getPath, getConfig, ...mutationOptions } = options;

  return useMutation<TData, ApiError, TVariables>({
    ...mutationOptions,
    mutationFn: (variables: TVariables) => {
      const path = getPath(variables);
      const config = getConfig?.(variables);
      return AwsApiClient.apiDelete<TData>(path, config);
    },
  });
}
